import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Mail, Calendar, Clock, Heart } from 'lucide-react';
import { siteConfig } from '../data/mock';

export const ThankYouPage = () => {
  const [searchParams] = useSearchParams();
  const packageName = searchParams.get('package') || 'coaching';
  const type = searchParams.get('type') || 'booking'; // booking, newsletter, contact

  const getContent = () => {
    switch (type) {
      case 'newsletter':
        return {
          title: 'You\'re In',
          subtitle: 'Welcome to the Collective',
          message: 'Thank you for joining. You\'ll receive the monthly collective reading directly in your inbox.',
          nextSteps: [
            'Check your inbox for a welcome message',
            'Add hello@thesoulscript.com to your contacts to ensure delivery',
            'The next collective reading arrives at the start of each month'
          ]
        };
      case 'contact':
        return {
          title: 'Message Received',
          subtitle: 'Thank You for Reaching Out',
          message: 'Your message has been received. I read every inquiry personally and will respond thoughtfully.',
          nextSteps: [
            'Expect a response within 2-3 business days',
            'Check your inbox (and spam folder) for my reply',
            'If your matter is urgent, please indicate so in a follow-up'
          ]
        };
      case 'tarot':
        return {
          title: 'Session Booked',
          subtitle: 'Thank You for Your Trust',
          message: `Your ${packageName} tarot session has been booked. This marks the beginning of a reflective space held with care, presence, and intention.`,
          nextSteps: [
            'You\'ll receive session details and video call link within 24 hours',
            'A calendar invitation will arrive with all necessary information',
            'Optional: Take a few quiet moments before your session to sit with your question',
            'Come as you are—there\'s no need to prepare anything specific'
          ],
          preSessionPrompt: 'Before your session, you might gently ask yourself: What am I truly seeking clarity on? What would it mean to receive insight here?'
        };
      default:
        return {
          title: 'Booking Confirmed',
          subtitle: 'Thank You for Your Trust',
          message: `Your ${packageName} package has been booked. This marks the beginning of our work together—a space held with care, intention, and respect for your journey.`,
          nextSteps: [
            'You\'ll receive detailed session information within 24 hours',
            'A calendar invitation will arrive with video call details',
            'Preparation notes will be included to help you arrive ready',
            'Email support begins immediately upon booking'
          ]
        };
    }
  };

  const content = getContent();

  return (
    <div className="bg-[var(--color-charcoal)] min-h-screen pt-32 pb-24">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 mx-auto mb-8 rounded-full bg-[var(--color-bottle-green)]/20 flex items-center justify-center"
          >
            <Check className="w-10 h-10 text-[var(--color-bottle-green-light)]" />
          </motion.div>

          <span className="text-[var(--color-gold)] text-sm tracking-[0.3em] uppercase mb-4 block">
            {content.subtitle}
          </span>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
            {content.title}
          </h1>
          <p className="text-lg text-[var(--color-cream)]/70 leading-relaxed max-w-2xl mx-auto">
            {content.message}
          </p>
        </motion.div>

        {/* What Happens Next */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="bg-[var(--color-charcoal-light)] rounded-lg p-8 lg:p-10 mb-12"
        >
          <h2 className="font-serif text-2xl mb-6 text-center">What Happens Next</h2>
          <div className="space-y-4">
            {content.nextSteps.map((step, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="w-6 h-6 rounded-full bg-[var(--color-gold)]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs text-[var(--color-gold)] font-medium">{index + 1}</span>
                </div>
                <p className="text-[var(--color-cream)]/70">{step}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Pre-Session Reflection Prompt (Tarot only) */}
        {type === 'tarot' && content.preSessionPrompt && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.8 }}
            className="mb-12 p-8 bg-[var(--color-deep-red)]/10 rounded-lg border border-[var(--color-deep-red)]/20"
          >
            <h3 className="font-serif text-xl mb-4 text-center text-[var(--color-gold)]">
              A Gentle Pre-Session Reflection
            </h3>
            <p className="text-[var(--color-cream)]/70 text-center italic leading-relaxed">
              "{content.preSessionPrompt}"
            </p>
          </motion.div>
        )}

        {/* Response Time & Boundaries */}
        {(type === 'booking' || type === 'tarot') && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12"
          >
            <div className="p-6 bg-[var(--color-charcoal-light)] rounded-lg border border-[var(--color-dark-purple)]/30">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-5 h-5 text-[var(--color-gold)]" />
                <h3 className="font-serif text-lg">Response Time</h3>
              </div>
              <p className="text-sm text-[var(--color-cream)]/60 leading-relaxed">
                Email support responses typically arrive within 24-48 hours. I respond thoughtfully, not reactively—quality over speed.
              </p>
            </div>

            <div className="p-6 bg-[var(--color-charcoal-light)] rounded-lg border border-[var(--color-dark-purple)]/30">
              <div className="flex items-center gap-3 mb-4">
                <Heart className="w-5 h-5 text-[var(--color-gold)]" />
                <h3 className="font-serif text-lg">A Note on Boundaries</h3>
              </div>
              <p className="text-sm text-[var(--color-cream)]/60 leading-relaxed">
                Our work together is held in a container of mutual respect. Clear boundaries support deeper work.
              </p>
            </div>
          </motion.div>
        )}

        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 text-[var(--color-cream)]/50 mb-8">
            <Mail className="w-4 h-4" />
            <span className="text-sm">{siteConfig.email}</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-[var(--color-gold)] text-[var(--color-gold)] rounded-full hover:bg-[var(--color-gold)] hover:text-[var(--color-charcoal)] transition-all duration-300"
            >
              Return Home
            </Link>
            {type !== 'booking' && (
              <Link
                to="/coaching"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[var(--color-gold)] text-[var(--color-charcoal)] font-medium rounded-full hover:bg-[var(--color-gold-light)] transition-all duration-300"
              >
                <Calendar className="w-4 h-4" />
                Explore Coaching
              </Link>
            )}
          </div>
        </motion.div>

        {/* Closing Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="mt-16 pt-12 border-t border-[var(--color-dark-purple)]/20 text-center"
        >
          <p className="font-serif text-xl text-[var(--color-cream)]/60 italic">
            "The journey inward begins with a single step of trust."
          </p>
          <p className="text-sm text-[var(--color-gold)] mt-4">
            — The Soul Script
          </p>
        </motion.div>
      </div>
    </div>
  );
};
